import { useMemo, useState } from "react";
import Cookies from "js-cookie";

import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { Avatar, Box, Divider, Grid, IconButton, List, ListItem, ListItemButton, ListItemIcon, ListItemText, Toolbar, Typography, Collapse } from "@mui/material";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import PropTypes from "prop-types";
import { styled } from "@mui/material/styles";

import { logOut } from "../../redux/auth/actions";
import { getCurrentUserSelector } from "../../redux/users/selector";
import { DrawerItems, MobileWidth, Roles } from "../../utilities/constants";
import useWindowDimensions from "../../utilities/hooks/useWindowDimensions";
import Drawer from "./Drawer";

const StyledIconButton = styled(IconButton, { shouldForwardProp: (prop) => prop !== "userRole" })(({ userRole, theme }) => ({
  position: "absolute",
  bottom: DrawerItems({ isFirstLogin: false, role: userRole }).filter((item) => !item.roles || item.roles.includes(userRole)).length > 7 ? "20%" : "20%",
  right: "0%",
  zIndex: "5000",
  border: "2px solid grey",
  edge: "start",
  [theme.breakpoints.down("sm")]: {
    bottom: DrawerItems({ isFirstLogin: false, role: userRole }).filter((item) => !item.roles || item.roles.includes(userRole)).length > 7 ? "20%" : "20%",
  },
}));

const AppMenuDrawer = ({ open, setOpen }) => {
  const dispatch = useDispatch();
  const user = useSelector(getCurrentUserSelector);
  const isFirstLogin = useSelector((state) => state.auth.isFirstLogin);
  const navigate = useNavigate();
  const { width } = useWindowDimensions();
  const location = useLocation();
  const [clickedItem, setClickedItem] = useState(null);

  const username = useMemo(() => {
    if (!user) {
      return null;
    }
    if (user.role === Roles.COMPANY.value) {
      return user?.company?.name ? user.company.name : user.email;
    }
    return user?.profile?.firstName && user?.profile?.lastName ? `${user.profile.firstName} ${user.profile.lastName}` : user.email;
  }, [user]);

  const userPicture = useMemo(() => {
    if (!user) {
      return null;
    }
    if (user.role === Roles.COMPANY.value && user?.company?.companyLogo?.fileUrl) {
      return <Avatar style={{ width: "45px" }} src={user.company.companyLogo.fileUrl} />;
    }
    if (user.role === Roles.USER.value && user?.profile?.profilePhoto?.fileUrl) {
      return <Avatar style={{ width: "45px" }} src={user.profile.profilePhoto.fileUrl} />;
    }
    return <AccountCircleIcon sx={{ height: "100%", verticalAlign: "middle" }} />;
  }, [user]);

  return (
    user && (
      <Drawer variant="permanent" open={open} width={width} anchor="left">
        <Toolbar />
        <StyledIconButton color="black" onClick={() => setOpen((oldVal) => !oldVal)} userRole={user.role}>
          {open ? <ArrowBackIosIcon sx={{ position: "relative", right: "-5px" }} /> : <ArrowForwardIosIcon />}
        </StyledIconButton>
        <List sx={{ padding: "15px 0", height: "100%", border: "0" }}>
          {DrawerItems({ isFirstLogin, role: user.role })
            .filter((item) => !item.roles || item.roles.includes(user?.role))
            .map((item) => (
              <Box
                key={item.title}
                sx={{
                  ...(item.title === "Sign Out" && {
                    position: "absolute",
                    bottom: "0",
                    width: "100%",
                    marginBottom: "100px",
                  }),
                }}>
                <ListItem
                  disablePadding
                  sx={{
                    display: "block",
                    paddingTop: "6px",
                    paddingBottom: "6px",
                    ...(location.pathname === item.title && {
                      backgroundColor: "rgba(255, 247, 217, 1)",
                    }),
                  }}>
                  <ListItemButton
                    onClick={() => {
                      if (width < MobileWidth) {
                        setOpen(false);
                      }
                      if (item.title === "Sign Out") {
                        dispatch(logOut())
                          .unwrap()
                          .then(() => {
                            Cookies.remove("isFromDashboard");
                            navigate("/login");
                          });
                      }
                      if (item.children) {
                        setClickedItem(clickedItem === item.title ? null : item.title);
                      } else {
                        Cookies.remove("isFromDashboard");
                        navigate(item.route);
                      }
                    }}
                    disabled={item?.disabled}
                    sx={{
                      minHeight: 30,
                      height: "40px",
                      justifyContent: open ? "initial" : "center",
                      px: 2.5,
                      ...(location.pathname === item.route && {
                        backgroundColor: "rgba(192,32,38,0.06)",
                      }),
                    }}>
                    <ListItemIcon
                      sx={{
                        minWidth: 0,
                        mr: open ? 1 : "auto",
                        justifyContent: "center",
                        color: "#1e2836",
                      }}>
                      <item.icon
                        style={{
                          ...(location.pathname === item.route && {
                            fill: "rgba(192,32,38,0.73)",
                          }),
                        }}
                      />
                    </ListItemIcon>
                    <ListItemText
                      sx={{
                        opacity: open ? 1 : 0,
                      }}>
                      <Typography
                        sx={{
                          ...(location.pathname === item.route ? { color: "rgba(192,32,38,0.73)" } : { color: "#1e2836" }),
                        }}
                        variant="h5"
                        fontWeight="500">
                        {item.title}
                      </Typography>
                    </ListItemText>
                    {item.children && open && (
                      <ExpandMoreIcon
                        sx={{
                          opacity: open ? 1 : 0,
                          transform: clickedItem === item.title ? "rotate(180deg)" : "rotate(0deg)",
                          transition: "transform 0.2s ease-in-out",
                        }}
                      />
                    )}
                  </ListItemButton>
                  {item.children && (
                    <Collapse
                      in={clickedItem === item.title && open}
                      timeout="auto"
                      unmountOnExit
                      sx={{
                        backgroundColor: "rgba(240, 240, 240, 0.5)",
                        marginLeft: "16px",
                        marginRight: "16px",
                        borderRadius: "4px",
                      }}>
                      <List component="div" disablePadding>
                        {item.children.map((childItem) => (
                          <ListItem
                            key={childItem.title}
                            disablePadding
                            sx={{
                              display: "block",
                              paddingTop: "2px",
                              paddingBottom: "2px",
                            }}>
                            <ListItemButton
                              onClick={() => {
                                if (width < MobileWidth) {
                                  setOpen(false);
                                }
                                // Check if the route is an external URL
                                if (childItem.route.startsWith("http://") || childItem.route.startsWith("https://")) {
                                  window.open(childItem.route, "_blank", "noopener,noreferrer");
                                } else {
                                  Cookies.remove("isFromDashboard");
                                  navigate(childItem.route);
                                }
                              }}
                              sx={{
                                minHeight: 30,
                                height: "35px",
                                justifyContent: "initial",
                                px: 2.5,
                                pl: 4,
                                ...(location.pathname === childItem.route && {
                                  backgroundColor: "rgba(192,32,38,0.06)",
                                }),
                              }}>
                              <ListItemText
                                sx={{
                                  opacity: 1,
                                }}>
                                <Typography
                                  sx={{
                                    ...(location.pathname === childItem.route ? { color: "rgba(192,32,38,0.73)" } : { color: "#1e2836" }),
                                    fontSize: "14px",
                                  }}
                                  variant="h6"
                                  fontWeight="500">
                                  {childItem.title}
                                </Typography>
                              </ListItemText>
                            </ListItemButton>
                          </ListItem>
                        ))}
                      </List>
                    </Collapse>
                  )}
                </ListItem>
              </Box>
            ))}
          <Box key="account-info-drawer-ite" position="absolute" bottom="0" width="100%">
            <Divider
              sx={{
                background: "#D4D4D4",
                borderBottomWidth: "inherit",
                height: "0.8px",
              }}
              light
            />
            <ListItem
              disablePadding
              sx={{
                display: "block",
                padding: "15px 10px",
              }}>
              {!open ? (
                <ListItemButton
                  sx={{
                    ":hover": {
                      background: "none",
                    },
                    minHeight: 30,
                    justifyContent: "center",
                    px: 2.5,
                    height: "100%",
                    verticalAlign: "middle",
                  }}>
                  {userPicture}
                </ListItemButton>
              ) : (
                <ListItemText sx={{ textAlign: "center" }}>
                  <Grid container>
                    <Grid item xs={2} sm={2} md={3} lg={3}>
                      {userPicture}
                    </Grid>
                    <Grid item xs={10} sm={10} md={9} lg={9} sx={{ whiteSpace: "break-spaces", wordWrap: "break-word", textAlign: "left" }}>
                      <Typography variant="h6" fontWeight="500" sx={{ marginTop: "6px" }}>
                        {username}
                      </Typography>
                    </Grid>
                  </Grid>
                </ListItemText>
              )}
            </ListItem>
          </Box>
        </List>
      </Drawer>
    )
  );
};

AppMenuDrawer.propTypes = {
  open: PropTypes.bool.isRequired,
  setOpen: PropTypes.func.isRequired,
};

export default AppMenuDrawer;
